import { useEffect, useState } from 'react';
import { Alert, Box, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Modal from '../../../components/atoms/modal/modal';
import Button from '../../../components/atoms/button';
import { toVariantPayload, validateVariantYears, validateVinRegex } from './functions';
import { VehicleVariant } from './types';
import { SaveToBackoffice } from '../../../utils/requests';

type EditVariantProps = {
  variant: VehicleVariant | null;
  brandId: number | '';
  brandName: string;
  brandPatterns: string[];
  modelName: string;
  open: boolean;
  onClose: () => void;
  onSaved: (variant: VehicleVariant) => void;
};

const EditVariant = ({ variant, brandId, brandName, brandPatterns, modelName, open, onClose, onSaved }: EditVariantProps) => {
  const [formData, setFormData] = useState<VehicleVariant | null>(variant);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFormData(variant);
    setError(null);
  }, [variant, open]);

  if (!formData) {
    return null;
  }

  const handleChange = (field: keyof VehicleVariant, value: string | number | null) => {
    setFormData((current) => current ? { ...current, [field]: value } : current);
  };

  const handleSave = async () => {
    const yearError = validateVariantYears(formData.fromYear, formData.toYear);
    if (yearError) {
      setError(yearError);
      return;
    }

    const regexPattern = formData.regexPattern.trim();
    const validationError = validateVinRegex(regexPattern, brandPatterns);
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = toVariantPayload({ ...formData, regexPattern });

    setSaving(true);
    setError(null);

    try {
      await SaveToBackoffice(`portal/vehicle/brands/${brandId}/models/${formData.modelId}/variants/${formData.variantId}`, {
        variantId: formData.variantId,
        brandId,
        modelId: formData.modelId,
        ...payload,
      });
      onSaved({ ...formData, ...payload });
      onClose();
    } catch (saveError) {
      console.error('Error updating vehicle variant', saveError);
      setError('Unable to save vehicle variant.');
    } finally {
      setSaving(false);
    }
  };

  const yearError = formData.fromYear === ''
    ? null
    : validateVariantYears(formData.fromYear, formData.toYear);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit vehicle variant"
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
      }
      size="medium"
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField label="Brand" value={brandName} disabled size="small" />
        <TextField label="Model" value={modelName} disabled size="small" />
        <TextField label="Variant" value={formData.name} onChange={(event) => handleChange('name', event.target.value)} size="small" />
        <FormControl size="small">
          <InputLabel>Powertrain</InputLabel>
          <Select
            value={formData.powertrain}
            label="Powertrain"
            onChange={(event) => handleChange('powertrain', event.target.value)}
          >
            <MenuItem value="ICE">ICE</MenuItem>
            <MenuItem value="EV">EV</MenuItem>
          </Select>
        </FormControl>
        <TextField label="From year" type="number" value={formData.fromYear} onChange={(event) => handleChange('fromYear', Number(event.target.value))} size="small" />
        <TextField
          label="To year"
          type="number"
          value={formData.toYear ?? ''}
          onChange={(event) => handleChange('toYear', event.target.value ? Number(event.target.value) : null)}
          size="small"
          error={Boolean(yearError)}
          helperText={yearError ?? 'Leave empty for ongoing production'}
        />
        <TextField label="Weight (kg)" type="number" value={formData.weight} onChange={(event) => handleChange('weight', Number(event.target.value))} size="small" />
        <TextField label="Length (mm)" type="number" value={formData.length} onChange={(event) => handleChange('length', Number(event.target.value))} size="small" />
        <TextField label="Width (mm)" type="number" value={formData.width} onChange={(event) => handleChange('width', Number(event.target.value))} size="small" />
        <TextField label="Height (mm)" type="number" value={formData.height} onChange={(event) => handleChange('height', Number(event.target.value))} size="small" />
        <TextField label="MonitorId" value={formData.externalId ?? ''} onChange={(event) => handleChange('externalId', event.target.value || null)} size="small" />
        <TextField label="VIN pattern" value={formData.regexPattern} onChange={(event) => handleChange('regexPattern', event.target.value)} size="small" />
      </Box>
    </Modal>
  );
};

export default EditVariant;
